// Ownship marker SVG strings — one per OwnshipMarker enum value.
//
// All silhouettes point UP (north / 0°). MapView rotates the parent
// element by the GPS heading. 34×34 viewBox, fire-orange fill,
// dark cockpit stroke, soft glow filter — same visual language as
// the original triangle.

import type { OwnshipMarker } from '../lib/aircraftTypes';

const FILL = '#ff5a1f';
const STROKE = '#0b0d0f';
const GLOW = 'drop-shadow(0 0 4px rgba(255,90,31,0.6))';

function wrap(inner: string): string {
  return `
  <div style="width:34px;height:34px;display:flex;align-items:center;justify-content:center;">
    <svg width="34" height="34" viewBox="0 0 34 34" style="filter: ${GLOW};">
      ${inner}
    </svg>
  </div>`;
}

// ─── Per-marker silhouettes ─────────────────────────────────────────────

// Generic triangle (legacy default).
const ARROW = `
  <polygon points="17,3 28,29 17,23 6,29" fill="${FILL}" stroke="${STROKE}" stroke-width="1.5"/>
  <circle cx="17" cy="20" r="2" fill="${STROKE}"/>`;

// High-wing single — wings ABOVE fuselage centerline, long thin wing.
const HIGH_WING = `
  <!-- fuselage -->
  <path d="M17 3 L19 11 L19 25 L17 30 L15 25 L15 11 Z" fill="${FILL}" stroke="${STROKE}" stroke-width="1.2"/>
  <!-- high wing -->
  <rect x="3" y="11" width="28" height="3.5" rx="1.2" fill="${FILL}" stroke="${STROKE}" stroke-width="1.2"/>
  <!-- horizontal stab -->
  <rect x="11" y="25" width="12" height="2.5" rx="0.8" fill="${FILL}" stroke="${STROKE}" stroke-width="1"/>`;

// Low-wing single — wings BELOW fuselage centerline, wider thicker wing.
const LOW_WING = `
  <!-- fuselage -->
  <path d="M17 3 L19 11 L20 22 L17 30 L14 22 L15 11 Z" fill="${FILL}" stroke="${STROKE}" stroke-width="1.2"/>
  <!-- low wing -->
  <rect x="4" y="17" width="26" height="4" rx="1.3" fill="${FILL}" stroke="${STROKE}" stroke-width="1.2"/>
  <!-- horizontal stab -->
  <rect x="11" y="25" width="12" height="2.5" rx="0.8" fill="${FILL}" stroke="${STROKE}" stroke-width="1"/>`;

// Twin — two engine nacelles on the wing.
const TWIN = `
  <!-- fuselage -->
  <path d="M17 3 L19 11 L19 25 L17 30 L15 25 L15 11 Z" fill="${FILL}" stroke="${STROKE}" stroke-width="1.2"/>
  <!-- wing -->
  <rect x="2" y="14" width="30" height="3.5" rx="1.2" fill="${FILL}" stroke="${STROKE}" stroke-width="1.2"/>
  <!-- left nacelle -->
  <rect x="6" y="11" width="3.5" height="9" rx="1.2" fill="${FILL}" stroke="${STROKE}" stroke-width="1"/>
  <!-- right nacelle -->
  <rect x="24.5" y="11" width="3.5" height="9" rx="1.2" fill="${FILL}" stroke="${STROKE}" stroke-width="1"/>
  <!-- horizontal stab -->
  <rect x="11" y="25" width="12" height="2.5" rx="0.8" fill="${FILL}" stroke="${STROKE}" stroke-width="1"/>`;

// Helicopter — main rotor disc + tail boom.
const HELICOPTER = `
  <!-- rotor disc -->
  <ellipse cx="17" cy="14" rx="13" ry="2.6" fill="${FILL}" stroke="${STROKE}" stroke-width="1" opacity="0.55"/>
  <!-- fuselage body -->
  <ellipse cx="17" cy="15" rx="4.5" ry="6" fill="${FILL}" stroke="${STROKE}" stroke-width="1.2"/>
  <!-- tail boom -->
  <rect x="15.7" y="20" width="2.6" height="9" fill="${FILL}" stroke="${STROKE}" stroke-width="1"/>
  <!-- tail rotor stab -->
  <rect x="13" y="28" width="8" height="1.8" rx="0.6" fill="${FILL}" stroke="${STROKE}" stroke-width="1"/>
  <!-- rotor hub -->
  <circle cx="17" cy="14" r="1.4" fill="${STROKE}"/>`;

// Glider — very long thin wings, slender fuselage, T-tail.
const GLIDER = `
  <!-- long thin wing -->
  <rect x="1" y="13.4" width="32" height="2.2" rx="1" fill="${FILL}" stroke="${STROKE}" stroke-width="1"/>
  <!-- slender fuselage -->
  <path d="M17 3 L18 10 L17.6 27 L17 30 L16.4 27 L16 10 Z" fill="${FILL}" stroke="${STROKE}" stroke-width="1"/>
  <!-- T-tail vertical -->
  <rect x="16.4" y="22" width="1.2" height="5" fill="${FILL}" stroke="${STROKE}" stroke-width="0.6"/>
  <!-- T-tail horizontal -->
  <rect x="11" y="25.5" width="12" height="1.8" rx="0.6" fill="${FILL}" stroke="${STROKE}" stroke-width="1"/>`;

// Jet — swept wings, T-tail, fuselage-mounted engines.
const JET = `
  <!-- fuselage -->
  <path d="M17 3 L19.5 11 L20 24 L17 30 L14 24 L14.5 11 Z" fill="${FILL}" stroke="${STROKE}" stroke-width="1.2"/>
  <!-- swept wing left -->
  <path d="M14.5 15 L2 24 L4 26 L15 20 Z" fill="${FILL}" stroke="${STROKE}" stroke-width="1"/>
  <!-- swept wing right -->
  <path d="M19.5 15 L32 24 L30 26 L19 20 Z" fill="${FILL}" stroke="${STROKE}" stroke-width="1"/>
  <!-- horizontal stab -->
  <path d="M14 26 L9 30 L11 30.5 L17 28 L23 30.5 L25 30 L20 26 Z" fill="${FILL}" stroke="${STROKE}" stroke-width="1"/>`;

const REGISTRY: Record<OwnshipMarker, string> = {
  'high-wing': HIGH_WING,
  'low-wing': LOW_WING,
  'twin': TWIN,
  'helicopter': HELICOPTER,
  'glider': GLIDER,
  'jet': JET,
  'arrow': ARROW,
};

/** Render the SVG markup for an OwnshipMarker style. Falls back to the
 *  generic arrow if the value is unrecognized (e.g. legacy data). */
export function ownshipMarkerSvg(marker: OwnshipMarker | string | undefined | null): string {
  if (marker && marker in REGISTRY) return wrap(REGISTRY[marker as OwnshipMarker]);
  return wrap(ARROW);
}
